import React, { useState, useEffect } from 'react';
import { apbdService } from '../../services/apbdService';
import {
    FaDownload, FaFilePdf, FaSpinner, FaCalendarAlt,
    FaChartBar, FaChartPie, FaChartLine, FaEye, FaFileAlt,
    FaTimes, FaExternalLinkAlt
} from 'react-icons/fa';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const APBDPage = () => {
    const [apbdList, setApbdList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedApbd, setSelectedApbd] = useState(null);
    const [activeView, setActiveView] = useState('chart');

    // ✅ State filter
    const [filterJenis, setFilterJenis] = useState(''); // '', 'murni', 'perubahan'
    const [filterTahun, setFilterTahun] = useState(''); // '', '2026', '2025', dst.

    // State untuk modal preview
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    useEffect(() => {
        loadApbdData();
    }, []);

    // Lock scroll body saat modal terbuka + ESC untuk close
    useEffect(() => {
        if (!previewOpen) return;

        const handleEsc = (e) => {
            if (e.key === 'Escape') closePreview();
        };

        document.addEventListener('keydown', handleEsc);
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = originalOverflow;
        };
    }, [previewOpen]);

    const loadApbdData = async () => {
        setLoading(true);
        try {
            const result = await apbdService.getLatestApbd();
            if (result.success) {
                const data = result.data?.data || result.data || [];
                const list = Array.isArray(data) ? data : [];

                // ✅ Normalisasi data: jenis → lowercase, angka → number
                const normalized = list.map(item => ({
                    ...item,
                    jenis: (item.jenis || 'murni').toString().toLowerCase().trim(),
                    total_pendapatan: Number(item.total_pendapatan) || 0,
                    total_belanja: Number(item.total_belanja) || 0,
                    total_pembiayaan: Number(item.total_pembiayaan) || 0,
                }));

                setApbdList(normalized);
                if (normalized.length > 0) {
                    setSelectedApbd(normalized[0]);
                }
            }
        } catch (error) {
            console.error('Error loading APBD:', error);
        }
        setLoading(false);
    };

    // ✅ Filter data berdasarkan jenis + tahun
    const filteredList = apbdList.filter(item => {
        const itemJenis = (item.jenis || 'murni').toString().toLowerCase().trim();
        const matchJenis = !filterJenis || itemJenis === filterJenis.toLowerCase();
        const matchTahun = !filterTahun || item.tahun?.toString() === filterTahun;
        return matchJenis && matchTahun;
    });

    // ✅ Daftar tahun unik (dari semua data, bukan hasil filter)
    const availableYears = [...new Set(apbdList.map(item => item.tahun))]
        .filter(Boolean)
        .sort((a, b) => b - a); // descending

    // ✅ Reset selectedApbd saat filter berubah
    useEffect(() => {
        if (filteredList.length > 0) {
            const exists = filteredList.find(a => a.id === selectedApbd?.id);
            if (!exists) setSelectedApbd(filteredList[0]);
        } else {
            setSelectedApbd(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterJenis, filterTahun]);

    // ============ SECURITY HELPERS ============
    const isSafeUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        try {
            const parsed = new URL(url, window.location.origin);
            return ['http:', 'https:'].includes(parsed.protocol);
        } catch {
            return false;
        }
    };

    const isPdfFile = (url, fileName) => {
        const target = (fileName || url || '').toLowerCase();
        return target.endsWith('.pdf');
    };

    // ============ PREVIEW ============
    const handlePreview = async (apbd) => {
        try {
            const tokenResult = await apbdService.requestDownloadToken(apbd.id, 'preview');

            if (!tokenResult?.success || !tokenResult?.download_url) {
                alert(tokenResult?.message || 'Gagal membuka preview');
                return;
            }

            const directUrl = tokenResult.download_url;
            const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(directUrl)}&embedded=true`;

            setPreviewUrl(googleViewerUrl);
            setPreviewTitle(apbd.judul || `APBD ${apbd.tahun}`);
            setPreviewOpen(true);

        } catch {
            alert('Gagal memuat preview');
        }
    };

    const closePreview = () => {
        setPreviewOpen(false);
        setPreviewUrl('');
        setPreviewTitle('');
    };

    // ============ DOWNLOAD ============
    const handleDownload = async (apbd) => {
        const fileName = apbd.file_name || `APBD-${apbd.tahun}.pdf`;

        try {
            const tokenResult = await apbdService.requestDownloadToken(apbd.id, 'download');

            if (!tokenResult?.success || !tokenResult?.download_url) {
                throw new Error(tokenResult?.message || 'Gagal mendapat token');
            }

            const downloadUrl = tokenResult.download_url;

            try {
                const response = await fetch(downloadUrl, {
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                });

                if (!response.ok) throw new Error(`HTTP ${response.status}`);

                const blob = await response.blob();
                if (blob.type.includes('text/html')) throw new Error('Response bukan file');

                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

                return;
            } catch (fetchErr) {
                console.warn('⚠️ Fetch gagal, fallback iframe:', fetchErr.message);
            }

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = downloadUrl;
            document.body.appendChild(iframe);
            setTimeout(() => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            }, 60000);

        } catch (err) {
            alert(`Gagal mengunduh: ${err.message}`);
        }
    };

    // ============ FORMATTER ============
    const formatRupiah = (angka) => {
        const num = Number(angka);
        if (!num || isNaN(num)) return 'Rp 0';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(num);
    };

    const formatRupiahShort = (angka) => {
        const num = Number(angka);
        if (!num || isNaN(num)) return '0';
        if (num >= 1_000_000_000_000) return (num / 1_000_000_000_000).toFixed(1) + ' T';
        if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + ' M';
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + ' Jt';
        if (num >= 1_000) return (num / 1_000).toFixed(0) + ' Rb';
        return num.toString();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'long', year: 'numeric'
        });
    };

    // ============ DATA CHARTS ============
    const comparisonData = filteredList.map(item => ({
        tahun: `${item.tahun}${item.jenis === 'perubahan' ? ' (P)' : ''}`,
        Pendapatan: Number(item.total_pendapatan) || 0,
        Belanja: Number(item.total_belanja) || 0,
        Pembiayaan: Number(item.total_pembiayaan) || 0,
    })).sort((a, b) => parseInt(a.tahun) - parseInt(b.tahun));

    const pieData = selectedApbd ? [
        { name: 'Pendapatan', value: Number(selectedApbd.total_pendapatan) || 0, color: '#16A34A' },
        { name: 'Belanja', value: Number(selectedApbd.total_belanja) || 0, color: '#2563EB' },
        { name: 'Pembiayaan', value: Number(selectedApbd.total_pembiayaan) || 0, color: '#9333EA' },
    ].filter(d => d.value > 0) : [];

    const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

    const trendData = comparisonData.map(item => ({
        tahun: item.tahun,
        Pendapatan: item.Pendapatan,
        Belanja: item.Belanja,
    }));

    // ============ LOADING ============
    if (loading) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                <p className="text-gray-500">Memuat data APBD...</p>
            </div>
        );
    }

    // ============ EMPTY GLOBAL ============
    if (apbdList.length === 0) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <FaFileAlt className="text-5xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Data APBD belum tersedia</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            {/* ============ MODAL PREVIEW ============ */}
            {previewOpen && (
                <div
                    className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={closePreview}
                >
                    <div
                        className="relative w-full max-w-6xl h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white">
                            <div className="flex items-center gap-3 min-w-0">
                                <FaFilePdf className="text-2xl flex-shrink-0" />
                                <div className="min-w-0">
                                    <h3 className="font-bold text-lg truncate">{previewTitle}</h3>
                                    <p className="text-xs opacity-80">Preview Dokumen</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hidden md:flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition"
                                >
                                    <FaExternalLinkAlt /> Buka Tab Baru
                                </a>
                                <button
                                    onClick={closePreview}
                                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 bg-gray-100 overflow-hidden">
                            <iframe
                                src={`${previewUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                                title={previewTitle}
                                className="w-full h-full border-0"
                            />
                        </div>

                        <div className="p-3 border-t bg-gray-50 flex justify-end gap-2">
                            <button
                                onClick={closePreview}
                                className="border border-gray-300 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm transition"
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Halaman */}
            <h1 className="text-3xl font-bold text-[#0F4C3A] mb-2">
                Anggaran Pendapatan dan Belanja Daerah (APBD)
            </h1>
            <p className="text-gray-600 mb-6">
                Data APBD Kota Magelang dari tahun ke tahun
            </p>

            {/* ✅ FILTER DROPDOWN */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <div className="flex flex-wrap gap-3 items-end">
                    {/* Filter Jenis */}
                    <div className="flex-1 min-w-[180px] max-w-xs">
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                            Jenis APBD
                        </label>
                        <select
                            value={filterJenis}
                            onChange={(e) => setFilterJenis(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-all"
                        >
                            <option value="">Semua Jenis</option>
                            <option value="murni">Data APBD</option>
                            <option value="perubahan">Data APBD Perubahan</option>
                        </select>
                    </div>

                    {/* Filter Tahun */}
                    <div className="flex-1 min-w-[180px] max-w-xs">
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                            Tahun
                        </label>
                        <select
                            value={filterTahun}
                            onChange={(e) => setFilterTahun(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent transition-all"
                        >
                            <option value="">Semua Tahun</option>
                            {availableYears.map(year => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Reset */}
                    {(filterJenis || filterTahun) && (
                        <button
                            onClick={() => {
                                setFilterJenis('');
                                setFilterTahun('');
                            }}
                            className="px-4 py-2.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1 border border-gray-200"
                        >
                            ✕ Reset Filter
                        </button>
                    )}
                </div>

                {/* Info hasil filter */}
                <p className="text-xs text-gray-400 mt-3">
                    Menampilkan <span className="font-semibold text-gray-600">{filteredList.length}</span> dari <span className="font-semibold text-gray-600">{apbdList.length}</span> data
                </p>
            </div>

            {/* Toggle View */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setActiveView('chart')}
                    className={`px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2 transition-all ${activeView === 'chart'
                            ? 'bg-[#0F4C3A] text-white shadow-md'
                            : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                        }`}
                >
                    <FaChartBar /> Diagram & Grafik
                </button>
                <button
                    onClick={() => setActiveView('list')}
                    className={`px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2 transition-all ${activeView === 'list'
                            ? 'bg-[#0F4C3A] text-white shadow-md'
                            : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                        }`}
                >
                    <FaFileAlt /> Daftar APBD
                </button>
            </div>

            {/* ✅ Empty state untuk filter kosong */}
            {filteredList.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <FaFileAlt className="text-5xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">
                        Tidak ada data APBD untuk filter yang dipilih
                    </p>
                    <p className="text-xs text-gray-400 mb-4">
                        {filterJenis && `Jenis: ${filterJenis === 'perubahan' ? 'APBD Perubahan' : 'APBD'}`}
                        {filterJenis && filterTahun && ' • '}
                        {filterTahun && `Tahun: ${filterTahun}`}
                    </p>
                    <button
                        onClick={() => {
                            setFilterJenis('');
                            setFilterTahun('');
                        }}
                        className="text-sm text-[#2E7D32] hover:text-[#4CAF50] underline"
                    >
                        Reset Filter
                    </button>
                </div>
            ) : (
                <>
                    {/* VIEW: CHART */}
                    {activeView === 'chart' && (
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl text-white shadow-lg">
                                    <h3 className="text-sm font-semibold opacity-90 mb-1">Total Pendapatan</h3>
                                    <p className="text-2xl font-bold">
                                        {formatRupiah(filteredList.reduce((sum, item) => sum + (Number(item.total_pendapatan) || 0), 0))}
                                    </p>
                                    <p className="text-xs opacity-75 mt-2">Dari {filteredList.length} data</p>
                                </div>
                                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl text-white shadow-lg">
                                    <h3 className="text-sm font-semibold opacity-90 mb-1">Total Belanja</h3>
                                    <p className="text-2xl font-bold">
                                        {formatRupiah(filteredList.reduce((sum, item) => sum + (Number(item.total_belanja) || 0), 0))}
                                    </p>
                                    <p className="text-xs opacity-75 mt-2">Dari {filteredList.length} data</p>
                                </div>
                                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl text-white shadow-lg">
                                    <h3 className="text-sm font-semibold opacity-90 mb-1">Total Pembiayaan</h3>
                                    <p className="text-2xl font-bold">
                                        {formatRupiah(filteredList.reduce((sum, item) => sum + (Number(item.total_pembiayaan) || 0), 0))}
                                    </p>
                                    <p className="text-xs opacity-75 mt-2">Dari {filteredList.length} data</p>
                                </div>
                            </div>

                            <div className="card-glass p-6">
                                <h2 className="text-xl font-bold text-[#0F4C3A] mb-4 flex items-center gap-2">
                                    <FaChartBar className="text-[#2E7D32]" />
                                    Perbandingan APBD Antar Tahun
                                </h2>
                                <div className="w-full h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={comparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                            <XAxis dataKey="tahun" tick={{ fontSize: 12 }} />
                                            <YAxis tickFormatter={formatRupiahShort} tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value) => formatRupiah(value)}
                                                contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                                            />
                                            <Legend />
                                            <Bar dataKey="Pendapatan" fill="#16A34A" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="Belanja" fill="#2563EB" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="Pembiayaan" fill="#9333EA" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="card-glass p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-xl font-bold text-[#0F4C3A] flex items-center gap-2">
                                            <FaChartPie className="text-[#2E7D32]" />
                                            Komposisi APBD
                                        </h2>
                                        <select
                                            value={selectedApbd?.id || ''}
                                            onChange={(e) => {
                                                const found = filteredList.find(a => String(a.id) === String(e.target.value));
                                                setSelectedApbd(found || null);
                                            }}
                                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2E7D32]"
                                        >
                                            {filteredList.map(item => (
                                                <option key={item.id} value={item.id}>
                                                    {item.tahun} {item.jenis === 'perubahan' ? '(Perubahan)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-full h-80">
                                        {pieTotal > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        labelLine={false}
                                                        label={({ name, percent }) =>
                                                            `${name}: ${((percent || 0) * 100).toFixed(1)}%`
                                                        }
                                                        outerRadius={100}
                                                        fill="#8884d8"
                                                        dataKey="value"
                                                    >
                                                        {pieData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(value) => formatRupiah(value)} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-gray-400">
                                                <p>Data komposisi tidak tersedia</p>
                                            </div>
                                        )}
                                    </div>
                                    {selectedApbd && (
                                        <div className="grid grid-cols-3 gap-3 mt-4">
                                            <div className="text-center p-3 bg-green-50 rounded-lg">
                                                <p className="text-xs text-gray-600 mb-1">Pendapatan</p>
                                                <p className="text-sm font-bold text-green-600">
                                                    {formatRupiahShort(selectedApbd.total_pendapatan)}
                                                </p>
                                            </div>
                                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                                                <p className="text-xs text-gray-600 mb-1">Belanja</p>
                                                <p className="text-sm font-bold text-blue-600">
                                                    {formatRupiahShort(selectedApbd.total_belanja)}
                                                </p>
                                            </div>
                                            <div className="text-center p-3 bg-purple-50 rounded-lg">
                                                <p className="text-xs text-gray-600 mb-1">Pembiayaan</p>
                                                <p className="text-sm font-bold text-purple-600">
                                                    {formatRupiahShort(selectedApbd.total_pembiayaan)}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="card-glass p-6">
                                    <h2 className="text-xl font-bold text-[#0F4C3A] mb-4 flex items-center gap-2">
                                        <FaChartLine className="text-[#2E7D32]" />
                                        Tren Pendapatan & Belanja
                                    </h2>
                                    <div className="w-full h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                                <XAxis dataKey="tahun" tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={formatRupiahShort} tick={{ fontSize: 12 }} />
                                                <Tooltip
                                                    formatter={(value) => formatRupiah(value)}
                                                    contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                                                />
                                                <Legend />
                                                <Line type="monotone" dataKey="Pendapatan" stroke="#16A34A" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
                                                <Line type="monotone" dataKey="Belanja" stroke="#2563EB" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* VIEW: LIST */}
                    {activeView === 'list' && (
                        <div className="space-y-6">
                            {filteredList.map((apbd) => {
                                const fileUrl = apbd.file_url || apbd.file_path || '';
                                const canAccess = isSafeUrl(fileUrl);
                                const isPdf = isPdfFile(fileUrl, apbd.file_name);

                                return (
                                    <div key={apbd.id} className="card-glass">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                                            <div className="flex items-center gap-3 flex-wrap mb-2 md:mb-0">
                                                <h2 className="text-2xl font-bold text-[#2E7D32]">
                                                    APBD Kota Magelang Tahun {apbd.tahun}
                                                </h2>
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${apbd.jenis === 'perubahan'
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {apbd.jenis === 'perubahan' ? 'APBD Perubahan' : 'APBD'}
                                                </span>
                                            </div>
                                            {apbd.published_at && (
                                                <span className="text-sm text-gray-500 flex items-center">
                                                    <FaCalendarAlt className="mr-1" />
                                                    {formatDate(apbd.published_at)}
                                                </span>
                                            )}
                                        </div>

                                        {apbd.deskripsi && (
                                            <p className="text-gray-600 mb-6">{apbd.deskripsi}</p>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                            <div className="bg-green-50 p-6 rounded-lg text-center">
                                                <h3 className="text-lg font-semibold text-gray-700 mb-2">Pendapatan</h3>
                                                <p className="text-2xl font-bold text-green-600">
                                                    {formatRupiah(apbd.total_pendapatan)}
                                                </p>
                                            </div>
                                            <div className="bg-blue-50 p-6 rounded-lg text-center">
                                                <h3 className="text-lg font-semibold text-gray-700 mb-2">Belanja</h3>
                                                <p className="text-2xl font-bold text-blue-600">
                                                    {formatRupiah(apbd.total_belanja)}
                                                </p>
                                            </div>
                                            <div className="bg-purple-50 p-6 rounded-lg text-center">
                                                <h3 className="text-lg font-semibold text-gray-700 mb-2">Pembiayaan</h3>
                                                <p className="text-2xl font-bold text-purple-600">
                                                    {formatRupiah(apbd.total_pembiayaan)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="border-t pt-4">
                                            <h3 className="text-lg font-bold text-[#2E7D32] mb-3">Dokumen APBD</h3>
                                            {apbd.file_name ? (
                                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center min-w-0">
                                                        <FaFilePdf className="text-red-500 text-2xl mr-3 flex-shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="font-semibold truncate">
                                                                {apbd.judul || `APBD ${apbd.tahun}`}
                                                            </p>
                                                            <p className="text-sm text-gray-500 truncate">{apbd.file_name}</p>
                                                            <p className="text-xs text-gray-400 flex items-center mt-1">
                                                                <FaCalendarAlt className="mr-1" />
                                                                {formatDate(apbd.published_at)} • {apbd.downloads || 0} downloads
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        {canAccess && isPdf && (
                                                            <button
                                                                onClick={() => handlePreview(apbd)}
                                                                className="text-[#2E7D32] hover:text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 border border-[#2E7D32] hover:bg-[#2E7D32] transition-colors"
                                                            >
                                                                <FaEye />
                                                                Preview
                                                            </button>
                                                        )}

                                                        {canAccess ? (
                                                            <button
                                                                onClick={() => handleDownload(apbd)}
                                                                className="bg-[#2E7D32] hover:bg-[#4CAF50] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                                            >
                                                                <FaDownload />
                                                                Download
                                                            </button>
                                                        ) : (
                                                            <span className="text-xs text-red-500 italic">
                                                                File tidak tersedia
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-gray-500">Belum ada dokumen</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default APBDPage;